import { useAlerts } from "components/Alerts/AlertProvider";
import { useCallback, useState } from "react";
import { downloadFromUrls } from "util/download";

import { useCheckForFile } from "./useCheckForFile";

/**
 * Shown when a key signs to nothing, which means the stored object is gone —
 * swept by a lifecycle rule, or cleaned up. The key is a bucket path carrying
 * the reader's tenant id, so it stays out of what they see.
 */
const MISSING_FILE_MESSAGE = "This file is no longer available. Run the export again.";

/**
 * Turns storage keys into downloads: signs each one, then starts the saves.
 *
 * Signing happens on the click rather than ahead of it, because a signature is
 * short-lived and the keys it is minted from can sit in a page (or a
 * notification) for days.
 *
 * `busy` stays true for the whole run — it can be a long one, since a multi-key
 * job spaces its clicks a second apart — so a caller can both show activity and
 * refuse a second run that would cancel the first one's provisional navigations.
 */
export function useDownloadFiles() {
  const { fetchFile } = useCheckForFile();
  const alerts = useAlerts();
  const [busy, setBusy] = useState(false);

  const download = useCallback(
    async (downloadKeys: string[]) => {
      if (busy || downloadKeys.length === 0) {
        return;
      }
      setBusy(true);
      try {
        // Resolve every signed URL before clicking any of them, so a key that
        // fails to sign surfaces its error before any file has downloaded.
        const signedUrls: string[] = [];
        for (const key of downloadKeys) {
          const signedUrl = await fetchFile(key);
          if (!signedUrl) {
            // Only the key says which file vanished, so it goes to the console.
            console.error("No stored file for export download key", key);
            throw new Error(MISSING_FILE_MESSAGE);
          }
          signedUrls.push(signedUrl);
        }
        await downloadFromUrls(signedUrls);
      } catch (error) {
        if (error instanceof Error) {
          alerts.error(error.message);
        } else {
          alerts.error("An unknown error occurred while downloading files");
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, fetchFile, alerts]
  );

  return { download, busy };
}
